import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import VerifiedTierBadge from "./VerifiedTierBadge";
import StarRating from "./StarRating";
import { hideOnError } from "../lib/imgFallback";
import { Sparkles } from "lucide-react";
import { displayName, displayInitial } from "../lib/displayName";

/**
 * FeaturedPractitionersRail — feed right-rail card. Deterministic
 * weekly rotation of Verified Initiates / Elders, served by
 * /api/providers/featured. Auto-hides if the pool is empty.
 */
export default function FeaturedPractitionersRail() {
  const [data, setData] = useState({ providers: [], week: "" });
  useEffect(() => {
    api.get("/providers/featured?limit=3").then(({ data }) => setData(data)).catch(() => {});
  }, []);
  if (!data.providers?.length) return null;

  return (
    <div
      className="border border-violet-300/30 bg-violet-400/[0.04] rounded-sm p-4 space-y-3"
      data-testid="featured-practitioners-rail"
    >
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-2 text-violet-200">
          <Sparkles size={13} strokeWidth={1.5} />
          <span className="label-mystic">Featured this week</span>
        </div>
        <span className="text-[10px] text-foreground/40 uppercase tracking-wider">{data.week}</span>
      </div>

      <div className="space-y-3">
        {data.providers.map((p) => (
          <Link
            key={p.userId}
            to={`/profile/${p.userId}`}
            className="block group"
            data-testid={`featured-card-${p.userId}`}
          >
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full overflow-hidden bg-card border border-violet-300/30 flex-shrink-0">
                {(p.avatarUrl || p.user?.avatarUrl) ? (
                  <img
                    src={p.avatarUrl || p.user?.avatarUrl}
                    alt=""
                    onError={hideOnError}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center font-serif text-lg text-violet-200">
                    {displayInitial(p)}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-serif text-base truncate group-hover:text-violet-200 transition-colors">
                  {displayName(p)}
                </div>
                <div className="inline-flex items-center gap-2 mt-0.5">
                  <VerifiedTierBadge tier={p.verifiedTier} size={11} compact />
                  {p.totalReviews > 0 && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-foreground/60">
                      <StarRating value={p.averageRating || 0} testid={`featured-rating-${p.userId}`} />
                      <span>{(p.averageRating || 0).toFixed(1)}</span>
                    </span>
                  )}
                </div>
                {p.tradition && (
                  <div className="text-[10px] text-foreground/50 truncate mt-0.5">{p.tradition}</div>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>

      <Link
        to="/discover?tier=initiate"
        className="block text-center text-[11px] uppercase tracking-[0.16em] text-violet-200/80 hover:text-violet-200 pt-2 border-t border-violet-300/15"
        data-testid="featured-see-all"
      >
        Discover all Verified Initiates →
      </Link>
    </div>
  );
}
